<template>
  <div class="rt-date-wrapper">
    <el-input
      v-if="!ranged"
      ref="reference"
      v-clickoutside="handleClose"
      v-bind="firstInputId"
      :class="'el-date-editor--' + type"
      :readonly="!editable || readonly || type === 'dates' || type === 'week'"
      :disabled="pickerDisabled"
      :size="pickerSize"
      :name="name"
      :placeholder="placeholder"
      :value="displayValue"
      :validate-event="false"
      class="el-date-editor"
      @focus="handleFocus"
      @click="handleClick"
      @keydown.native="handlePickerKeydown"
      @input="(value) => (userInput = value)"
      @change="handleChange"
      @mouseenter.native="handleMouseEnter"
      @mouseleave.native="showClose = false"
    >
      <i
        slot="prefix"
        :class="triggerClass"
        class="el-input__icon"
        @click="handleClick"
      >
      </i>
      <i
        v-if="haveTrigger"
        slot="suffix"
        :class="[showClose ? '' + clearIcon : '']"
        class="el-input__icon"
        @click="handleClickIcon"
      >
      </i>
    </el-input>
    <div
      v-else
      ref="reference"
      v-clickoutside="handleClose"
      :class="[
        'el-date-editor--' + type,
        pickerSize ? `el-range-editor--${pickerSize}` : '',
        pickerDisabled ? 'is-disabled' : '',
        pickerVisible || hasFocus ? 'is-active' : '',
        hasFocus ? 'has-focus' : ''
      ]"
      class="el-date-editor el-range-editor el-input__inner"
      @click="handleRangeClick"
      @mouseenter="handleMouseEnter"
      @mouseleave="showClose = false"
      @keydown="handlePickerKeydown"
    >
      <input
        :placeholder="startPlaceholder"
        :value="displayValue && displayValue[0]"
        :disabled="pickerDisabled"
        v-bind="firstInputId"
        :readonly="!editable || readonly"
        :name="name && name[0]"
        autocomplete="off"
        class="rt-xpc-range-input el-range-input"
        @input="handleStartInput"
        @change="handleStartChange"
        @focus="handleStartFocus"
        @blur="handleStartBlur"
      />
      <slot name="range-separator">
        <span class="rt-xpc-range-separator el-range-separator">{{
          rangeSeparator
        }}</span>
      </slot>
      <input
        :placeholder="endPlaceholder"
        :value="displayValue && displayValue[1]"
        :disabled="pickerDisabled"
        v-bind="secondInputId"
        :readonly="!editable || readonly"
        :name="name && name[1]"
        autocomplete="off"
        class="rt-xpc-range-input rt-xpc-range-end__input el-range-input"
        @input="handleEndInput"
        @change="handleEndChange"
        @focus="handleEndFocus"
        @blur="handleEndBlur"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Clickoutside from 'element-ui/lib/utils/clickoutside';
import { formatDate, parseDate, isDateObject, getWeekNumber } from 'element-ui/lib/utils/date-util';
import Popper from 'element-ui/lib/utils/vue-popper';
import Emitter from 'element-ui/lib/mixins/emitter';
import ElInput from '../input/src/input.vue';
import merge from 'element-ui/lib/utils/merge';
import Keydown from './keydown.js'

const NewPopper = {
  props: {
    appendToBody: Popper.props.appendToBody,
    offset: Popper.props.offset,
    boundariesPadding: Popper.props.boundariesPadding,
    arrowOffset: Popper.props.arrowOffset
  },
  methods: Popper.methods,
  data () {
    return merge({ visibleArrow: true }, Popper.data);
  },
  beforeDestroy: Popper.beforeDestroy
};

const DEFAULT_FORMATS = {
  date: 'yyyy-MM-dd',
  month: 'yyyy-MM',
  datetime: 'yyyy-MM-dd HH:mm:ss',
  time: 'HH:mm:ss',
  week: 'yyyywWW',
  timerange: 'HH:mm:ss',
  daterange: 'yyyy-MM-dd',
  monthrange: 'yyyy-MM',
  datetimerange: 'yyyy-MM-dd HH:mm:ss',
  year: 'yyyy'
};
const HAVE_TRIGGER_TYPES = [
  'date',
  'datetime',
  'time',
  'time-select',
  'week',
  'month',
  'year',
  'daterange',
  'monthrange',
  'timerange',
  'datetimerange',
  'dates'
];
const DATE_FORMATTER = function (value, format) {
  if (format === 'timestamp') return value.getTime();
  return formatDate(value, format);
};
const DATE_PARSER = function (text, format) {
  if (format === 'timestamp') return new Date(Number(text));
  return parseDate(text, format);
};
const RANGE_FORMATTER = function (value, format) {
  if (Array.isArray(value) && value.length === 2) {
    const start = value[0];
    const end = value[1];

    if (start && end) {
      return [DATE_FORMATTER(start, format), DATE_FORMATTER(end, format)];
    }
  }
  return '';
};
const RANGE_PARSER = function (array, format, separator) {
  if (!Array.isArray(array)) {
    array = array.split(separator);
  }
  if (array.length === 2) {
    const range1 = array[0];
    const range2 = array[1];

    return [DATE_PARSER(range1, format), DATE_PARSER(range2, format)];
  }
  return [];
};
const TYPE_VALUE_RESOLVER_MAP = {
  default: {
    formatter (value) {
      if (!value) return '';
      return '' + value;
    },
    parser (text) {
      if (text === undefined || text === '') return null;
      return text;
    }
  },
  week: {
    formatter (value, format) {
      let week = getWeekNumber(value);
      let month = value.getMonth();
      const trueDate = new Date(value);
      if (week === 1 && month === 11) {
        trueDate.setHours(0, 0, 0, 0);
        trueDate.setDate(trueDate.getDate() + 3 - ((trueDate.getDay() + 6) % 7));
      }
      let date = formatDate(trueDate, format);
      const w = week < 10 ? '0' + week : week;

      date = /WW/.test(date) ? date.replace(/WW/, w) : date.replace(/W/, week);
      return date;
    },
    parser (text, format) {
      // parse as if a normal date
      return TYPE_VALUE_RESOLVER_MAP.date.parser(text, format);
    }
  },
  date: {
    formatter: DATE_FORMATTER,
    parser: DATE_PARSER
  },
  datetime: {
    formatter: DATE_FORMATTER,
    parser: DATE_PARSER
  },
  daterange: {
    formatter: RANGE_FORMATTER,
    parser: RANGE_PARSER
  },
  monthrange: {
    formatter: RANGE_FORMATTER,
    parser: RANGE_PARSER
  },
  datetimerange: {
    formatter: RANGE_FORMATTER,
    parser: RANGE_PARSER
  },
  timerange: {
    formatter: RANGE_FORMATTER,
    parser: RANGE_PARSER
  },
  time: {
    formatter: DATE_FORMATTER,
    parser: DATE_PARSER
  },
  month: {
    formatter: DATE_FORMATTER,
    parser: DATE_PARSER
  },
  year: {
    formatter: DATE_FORMATTER,
    parser: DATE_PARSER
  },
  number: {
    formatter (value) {
      if (!value) return '';
      return '' + value;
    },
    parser (text) {
      let result = Number(text);

      if (!isNaN(text)) {
        return result;
      } else {
        return null;
      }
    }
  },
  dates: {
    formatter (value, format) {
      return value.map(date => DATE_FORMATTER(date, format));
    },
    parser (value, format) {
      return (typeof value === 'string' ? value.split(', ') : value).map(date =>
        date instanceof Date ? date : DATE_PARSER(date, format)
      );
    }
  }
};
const PLACEMENT_MAP = {
  left: 'bottom-start',
  center: 'bottom',
  right: 'bottom-end'
};

const POS_DATETIME_MINUTE = {
  start: 14,
  end: 16
};

const POS_DATETIME_SECOND = {
  start: 17,
  end: 19
};

const POS_TIME_MINUTE = {
  start: 3,
  end: 5
};

const POS_TIME_SECOND = {
  start: 6,
  end: 8
};

const parseAsFormatAndType = (value, customFormat, type, rangeSeparator = '-') => {
  if (!value) return null;
  const parser = (TYPE_VALUE_RESOLVER_MAP[type] || TYPE_VALUE_RESOLVER_MAP['default']).parser;
  const format = customFormat || DEFAULT_FORMATS[type];
  return parser(value, format, rangeSeparator);
};

const formatAsFormatAndType = (value, customFormat, type) => {
  if (!value) return null;
  const formatter = (TYPE_VALUE_RESOLVER_MAP[type] || TYPE_VALUE_RESOLVER_MAP['default']).formatter;
  const format = customFormat || DEFAULT_FORMATS[type];
  return formatter(value, format);
};

/*
 * Considers:
 *   1. Date object
 *   2. date string
 *   3. array of 1 or 2
 */
const valueEquals = function (a, b) {
  // considers Date object and string
  const dateEquals = function (_a, _b) {
    const aIsDate = _a instanceof Date;
    const bIsDate = _b instanceof Date;
    if (aIsDate && bIsDate) {
      return _a.getTime() === _b.getTime();
    }
    if (!aIsDate && !bIsDate) {
      return _a === _b;
    }
    return false;
  };

  const aIsArray = a instanceof Array;
  const bIsArray = b instanceof Array;
  if (aIsArray && bIsArray) {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((item, index) => dateEquals(item, b[index]));
  }
  if (!aIsArray && !bIsArray) {
    return dateEquals(a, b);
  }
  return false;
};

const isString = function (val) {
  return typeof val === 'string' || val instanceof String;
};

const validator = function (val) {
  // either: String, Array of String, null / undefined
  return (
    val === null ||
    val === undefined ||
    isString(val) ||
    (Array.isArray(val) && val.length === 2 && val.every(isString))
  );
};

export default {
  components: { ElInput },

  directives: { Clickoutside },
  mixins: [Emitter, NewPopper, Keydown],

  inject: {
    elForm: {
      default: ''
    },
    elFormItem: {
      default: ''
    },
    rtTimePicker: {
      default: ''
    }
  },

  props: {
    size: String,
    format: String,
    valueFormat: String,
    readonly: Boolean,
    placeholder: String,
    startPlaceholder: String,
    endPlaceholder: String,
    prefixIcon: String,
    clearIcon: {
      type: String,
      default: 'el-icon-circle-close'
    },
    name: {
      default: '',
      validator
    },
    disabled: Boolean,
    clearable: {
      type: Boolean,
      default: undefined
    },
    id: {
      default: '',
      validator
    },
    popperClass: String,
    editable: {
      type: Boolean,
      default: true
    },
    align: {
      type: String,
      default: 'left'
    },
    // eslint-disable-next-line vue/require-prop-types
    value: {},
    // eslint-disable-next-line vue/require-prop-types
    defaultValue: {},
    // eslint-disable-next-line vue/require-prop-types
    defaultTime: {},
    // eslint-disable-next-line vue/require-prop-types
    rangeSeparator: {
      default: '-'
    },
    // eslint-disable-next-line vue/require-prop-types
    pickerOptions: {},
    unlinkPanels: Boolean,
    validateEvent: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      pickerVisible: false,
      showClose: false,
      userInput: null,
      valueOnOpen: null, // value when picker opens, used to determine whether to emit change
      unwatchPickerOptions: null,
      startInputFocused: false,
      endInputFocused: false
    };
  },

  computed: {
    hasFocus () {
      return this.startInputFocused || this.endInputFocused;
    },
    computedClearable () {
      return this.clearable === undefined ? this.elForm && this.elForm.$attrs && this.elForm.$attrs['all-clearable'] : this.clearable
    },
    showSeconds () {
      let format;
      if (this.type === 'time') format = 'HH:mm:ss';
      if (this.type === 'date') format = 'yyyy-MM-dd';
      if (this.type === 'datetime' || this.type === 'datetimerange') format = 'yyyy-MM-dd HH:mm:ss';

      return (this.format || format || '').indexOf('ss') !== -1;
    },
    /**
     * @private 跨度
     * 1. stepper 为 Number 取值当前所传值
     * 2. stepper 为 Boolean 取值 0
     */
    _computedStepper () {
      if (typeof this.stepper === 'number') {
        return this.stepper;
      }
      return this.stepper ? 10 : 0;
    },
    ranged () {
      return this.type && this.type.indexOf('range') > -1;
    },

    reference () {
      const reference = this.$refs.reference;
      return reference.$el || reference;
    },

    refInput () {
      if (this.reference) {
        return [].slice.call(this.reference.querySelectorAll('input'));
      }
      return [];
    },

    valueIsEmpty () {
      const val = this.value;
      if (Array.isArray(val)) {
        for (let i = 0, len = val.length; i < len; i++) {
          if (val[i]) {
            return false;
          }
        }
      } else {
        if (val) {
          return false;
        }
      }
      return true;
    },

    triggerClass () {
      return this.prefixIcon || (this.type.indexOf('time') !== -1 ? 'el-icon-time' : 'el-icon-date');
    },

    selectionMode () {
      if (this.type === 'week') {
        return 'week';
      } else if (this.type === 'month') {
        return 'month';
      } else if (this.type === 'year') {
        return 'year';
      } else if (this.type === 'dates') {
        return 'dates';
      }

      return 'day';
    },

    haveTrigger () {
      if (typeof this.showTrigger !== 'undefined') {
        return this.showTrigger;
      }
      return HAVE_TRIGGER_TYPES.indexOf(this.type) !== -1;
    },

    displayValue () {
      let formattedValue = formatAsFormatAndType(this.parsedValue, this.format, this.type);
      if (this.type === 'time' && this.pickerOptions && this.pickerOptions.format === 'A') {
        formattedValue = formatAsFormatAndType(this.parsedValue, this.rtTimePicker.format, this.type);
        if (this.value && this.rtTimePicker.format.includes('hh')) {
          let mode = this.value.getHours() > 12 ? 'PM' : 'AM';
          formattedValue = formattedValue + ' ' + mode;
        }
      }
      if (this.type === 'time-select') {
        formattedValue = this.value ? this.picker && this.picker.keyboardValue : this.value;
      }
      if (Array.isArray(this.userInput)) {
        return [
          this.userInput[0] || (formattedValue && formattedValue[0]) || '',
          this.userInput[1] || (formattedValue && formattedValue[1]) || ''
        ];
      } else if (this.userInput !== null) {
        return this.userInput;
      } else if (formattedValue) {
        // if (this._computedStepper) {
        //   return this._displayValueStepper(formattedValue);
        // } else {
        return this.type === 'dates' ? formattedValue.join(', ') : formattedValue;
        // }
      }
      return '';
    },

    parsedValue () {
      if (!this.value) return this.value; // component value is not set
      if (this.type === 'time-select') return this.value; // time-select does not require parsing, this might change in next major version

      const valueIsDateObject =
        isDateObject(this.value) || (Array.isArray(this.value) && this.value.every(isDateObject));
      if (valueIsDateObject) {
        return this.value;
      }

      if (this.valueFormat) {
        return parseAsFormatAndType(this.value, this.valueFormat, this.type, this.rangeSeparator) || this.value;
      }

      // NOTE: deal with common but incorrect usage, should remove in next major version
      // user might provide string / timestamp without value-format, coerce them into date (or array of date)
      return Array.isArray(this.value) ? this.value.map(val => new Date(val)) : new Date(this.value);
    },

    _elFormItemSize () {
      return (this.elFormItem || {}).elFormItemSize;
    },

    pickerSize () {
      return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
    },

    pickerDisabled () {
      return this.disabled || (this.elForm || {}).disabled;
    },

    firstInputId () {
      const obj = {};
      let id;
      if (this.ranged) {
        id = this.id && this.id[0];
      } else {
        id = this.id;
      }
      if (id) obj.id = id;
      return obj;
    },

    secondInputId () {
      const obj = {};
      let id;
      if (this.ranged) {
        id = this.id && this.id[1];
      }
      if (id) obj.id = id;
      return obj;
    }
  },

  watch: {
    pickerVisible (val) {
      if (this.readonly || this.pickerDisabled) return;
      if (val) {
        this.showPicker();
        this.valueOnOpen = Array.isArray(this.value) ? [...this.value] : this.value;
      } else {
        this.hidePicker();
        this.emitChange(this.value);
        this.userInput = null;
        if (this.validateEvent) {
          this.dispatch('ElFormItem', 'el.form.blur');
        }
        this.$emit('blur', this);
        this.$setTimeout(() => {
          if (this.ranged && !this.value) {
            this.refInput && this.refInput[0] && this.refInput[0].focus();
          } else {
            this.refInput.forEach(input => {
              input.focus();
            });
          }
        }, 0)
      }
      this.$emit('visible-change', val)
    },
    parsedValue: {
      immediate: true,
      handler (val) {
        if (this.picker) {
          this.picker.value = val;
        }
      }
    },
    defaultValue (val) {
      // NOTE: should eventually move to jsx style picker + panel ?
      if (this.picker) {
        this.picker.defaultValue = val;
      }
    },
    value (val, oldVal) {
      if (!valueEquals(val, oldVal) && !this.pickerVisible && this.validateEvent) {
        this.dispatch('ElFormItem', 'el.form.change', val);
      }
    }
  },

  created () {
    // vue-popper
    this.popperOptions = {
      boundariesPadding: 5,
      gpuAcceleration: false
    };
    this.placement = PLACEMENT_MAP[this.align] || PLACEMENT_MAP.left;

    this.$on('fieldReset', this.handleFieldReset);
  },

  methods: {
    /**
     * @private
     * 获取不同类型选择器的 分钟 / 秒索引
     */
    _getValueIndex () {
      if (this.type === 'datetime' || this.type === 'datetimerange') {
        return this.showSeconds
          ? { startIndex: POS_DATETIME_SECOND.start, endIndex: POS_DATETIME_SECOND.end }
          : { startIndex: POS_DATETIME_MINUTE.start, endIndex: POS_DATETIME_MINUTE.end };
      } else if (this.type === 'time' || this.isRange) {
        return this.showSeconds
          ? { startIndex: POS_TIME_SECOND.start, endIndex: POS_TIME_SECOND.end }
          : { startIndex: POS_TIME_MINUTE.start, endIndex: POS_TIME_MINUTE.end };
      }
    },
    /**
     * @private
     * 定义跨度后，更新 formattedValue
     */
    _stepperFormattedValue (formattedValue) {
      // 获取自定义跨度值
      let stepper = this._computedStepper;
      let startIndex = this._getValueIndex().startIndex;
      let endIndex = this._getValueIndex().endIndex;
      // 当前时间 分钟 / 秒
      let currentTime = +formattedValue.slice(startIndex, endIndex);
      // 跨度值, 比如：[00, 15, 30, 45]
      let stepValue = stepper * Math.floor(currentTime / stepper);
      return formattedValue.slice(0, startIndex) + (stepValue < 10 ? '0' + stepValue : stepValue);
    },
    /**
     * @private
     * 处理分钟 / 秒跨度 input 值
     */
    _displayValueStepper (formattedValue) {
      if (this.type === 'datetime' || this.type === 'time') {
        return this._stepperFormattedValue(formattedValue);
      } else if (this.type === 'datetimerange' || this.isRange) {
        return [this._stepperFormattedValue(formattedValue[0]), this._stepperFormattedValue(formattedValue[1])];
      }
    },
    focus () {
      if (!this.ranged) {
        this.$refs.reference.focus();
      } else {
        this.handleClick();
      }
    },

    blur () {
      this.refInput.forEach(input => input.blur());
    },

    // {parse, formatTo} Value deals maps component value with internal Date
    parseValue (value) {
      const isParsed = isDateObject(value) || (Array.isArray(value) && value.every(isDateObject));
      if (this.valueFormat && !isParsed) {
        return parseAsFormatAndType(value, this.valueFormat, this.type, this.rangeSeparator) || value;
      } else {
        return value;
      }
    },

    formatToValue (date) {
      const isFormattable = isDateObject(date) || (Array.isArray(date) && date.every(isDateObject));
      if (this.valueFormat && isFormattable) {
        return formatAsFormatAndType(date, this.valueFormat, this.type);
      } else {
        return date;
      }
    },

    // {parse, formatTo} String deals with user input
    parseString (value) {
      const type = Array.isArray(value) ? this.type : this.type.replace('range', '');
      return parseAsFormatAndType(value, this.format, type);
    },

    formatToString (value) {
      const type = Array.isArray(value) ? this.type : this.type.replace('range', '');
      return formatAsFormatAndType(value, this.format, type);
    },

    handleMouseEnter () {
      if (this.readonly || this.pickerDisabled) return;
      if (!this.valueIsEmpty && this.computedClearable) {
        this.showClose = true;
      }
    },

    handleChange () {
      if (this.userInput) {
        const value = this.parseString(this.displayValue);
        if (value) {
          this.picker.value = value;
          if (this.isValidValue(value)) {
            this.emitInput(value);
            this.userInput = null;
          }
        }
      }
      if (this.userInput === '') {
        this.emitInput(null);
        this.emitChange(null);
        this.userInput = null;
      }
    },

    handleStartInput (event) {
      if (this.userInput) {
        this.userInput = [event.target.value, this.userInput[1]];
      } else {
        this.userInput = [event.target.value, null];
      }
    },

    handleEndInput (event) {
      if (this.userInput) {
        this.userInput = [this.userInput[0], event.target.value];
      } else {
        this.userInput = [null, event.target.value];
      }
    },

    handleStartChange (event) {
      const value = this.parseString(this.userInput && this.userInput[0]);
      if (value) {
        this.userInput = [this.formatToString(value), this.displayValue[1]];
        const newValue = [value, this.picker.value && this.picker.value[1]];
        this.picker.value = newValue;
        if (this.isValidValue(newValue)) {
          this.emitInput(newValue);
          this.userInput = null;
        }
      }
    },

    handleEndChange (event) {
      const value = this.parseString(this.userInput && this.userInput[1]);
      if (value) {
        this.userInput = [this.displayValue[0], this.formatToString(value)];
        const newValue = [this.picker.value && this.picker.value[0], value];
        this.picker.value = newValue;
        if (this.isValidValue(newValue)) {
          this.emitInput(newValue);
          this.userInput = null;
        }
      }
    },

    handleClickIcon (event) {
      if (this.readonly || this.pickerDisabled) return;
      if (this.showClose) {
        this.valueOnOpen = this.value;
        event.stopPropagation();
        this.emitInput(null);
        this.emitChange(null);
        this.showClose = false;
        if (this.picker && typeof this.picker.handleClear === 'function') {
          this.picker.handleClear();
        }
      } else {
        this.pickerVisible = !this.pickerVisible;
      }
    },

    handleClose () {
      if (!this.pickerVisible) return;
      this.pickerVisible = false;

      if (this.type === 'dates') {
        // restore to former value
        const oldValue =
          parseAsFormatAndType(this.valueOnOpen, this.valueFormat, this.type, this.rangeSeparator) || this.valueOnOpen;
        this.emitInput(oldValue);
      }
    },

    handleFieldReset (initialValue) {
      this.userInput = initialValue === '' ? null : initialValue;
    },

    handleFocus () {
      this.$emit('focus', this);
    },

    handleStartFocus () {
      this.$emit('focus', this);
      this.startInputFocused = true;
    },

    handleEndFocus () {
      this.$emit('focus', this);
      this.endInputFocused = true;
    },

    handleStartBlur () {
      this.$emit('blur', this);
      this.startInputFocused = false;
    },

    handleEndBlur () {
      this.$emit('blur', this);
      this.endInputFocused = false;
    },

    handleClick () {
      const type = this.type;
      this.pickerVisible = false;

      if (HAVE_TRIGGER_TYPES.indexOf(type) !== -1 && !this.pickerVisible) {
        this.pickerVisible = true;
        this.$emit('visible-change', this.pickerVisible)

        this.showPicker();
      }
    },

    handleRangeClick () {
      const type = this.type;

      if (HAVE_TRIGGER_TYPES.indexOf(type) !== -1 && !this.pickerVisible) {
        this.pickerVisible = true;
      }
      this.$emit('focus', this);
    },

    hidePicker () {
      if (this.picker) {
        this.picker.resetView && this.picker.resetView();
        this.pickerVisible = this.picker.visible = false;
        this.destroyPopper();
      }
    },

    showPicker () {
      if (this.$isServer) return;
      if (!this.picker) {
        this.mountPicker();
      }
      this.pickerVisible = this.picker.visible = true;

      this.updatePopper();

      this.picker.value = this.parsedValue;
      this.picker.resetView && this.picker.resetView();

      this.$nextTick(() => {
        this.picker.adjustSpinners && this.picker.adjustSpinners();
      });
    },

    mountPicker () {
      this.picker = new Vue(this.panel).$mount();
      this.picker.defaultValue = this.defaultValue;
      this.picker.defaultTime = this.defaultTime;
      this.picker.popperClass = this.popperClass;
      this.popperElm = this.picker.$el;
      this.picker.width = this.reference.getBoundingClientRect().width;
      this.picker.showTime = this.type === 'datetime' || this.type === 'datetimerange';
      this.picker.selectionMode = this.selectionMode;
      this.picker.unlinkPanels = this.unlinkPanels;
      this.picker.arrowControl = this.arrowControl || this.timeArrowControl || false;
      this.picker.$slots = this.$slots;
      this.picker.now = this.now; // 此刻
      this.picker.stepper = this.stepper; // 跨度
      this.picker.clickNowShowPicker = this.clickNowShowPicker;
      this.picker.footerAlign = this.footerAlign;
      this.picker.type = this.type;

      this.$watch('format', format => {
        this.picker.format = format;
      });

      const updateOptions = () => {
        const options = this.pickerOptions;

        if (options && options.selectableRange) {
          let ranges = options.selectableRange;
          const parser = TYPE_VALUE_RESOLVER_MAP.datetimerange.parser;
          const format = DEFAULT_FORMATS.timerange;

          ranges = Array.isArray(ranges) ? ranges : [ranges];
          this.picker.selectableRange = ranges.map(range => parser(range, format, this.rangeSeparator));
        }

        // eslint-disable-next-line
        for (const option in options) {
          if (
            options.hasOwnProperty(option) &&
            // 忽略 time-picker 的该配置项
            option !== 'selectableRange'
          ) {
            this.picker[option] = options[option];
          }
        }

        // main format must prevail over undocumented pickerOptions.format
        if (this.format) {
          this.picker.format = this.format;
        }
      };
      updateOptions();
      this.unwatchPickerOptions = this.$watch('pickerOptions', () => updateOptions(), { deep: true });
      this.$el.appendChild(this.picker.$el);
      this.picker.resetView && this.picker.resetView();

      this.picker.$on('dodestroy', this.doDestroy);
      this.picker.$on('pick', (date = '', visible = false) => {
        this.userInput = null;
        this.pickerVisible = this.picker.visible = visible;
        this.emitInput(date);
        this.picker.resetView && this.picker.resetView();
      });

      this.picker.$on('visible-change', (val) => {
        this.$emit('visible-change', val)
      });

      this.picker.$on('time-confirm', (val) => {
        this.$emit('time-confirm', val)
      });
      this.picker.$on('time-cancel', (val) => {
        this.$emit('time-cancel', val)
      });
      this.picker.$on('date-confirm', (val) => {
        this.$emit('date-confirm', val)
      });
      this.picker.$on('datetime-cancel', (val) => {
        this.$emit('datetime-cancel', val)
      });
      this.picker.$on('date-range-confirm', (val) => {
        this.$emit('date-range-confirm', val)
      });
      this.picker.$on('change-now', (val) => {
        this.$emit('change-now', val)
      });
      this.picker.$on('shortcut', (val) => {
        this.$emit('shortcut', val)
      });
      this.picker.$on('keydown', (val) => {
        this.$emit('keydown', val)
      });

      this.picker.$on('select-range', (start, end, pos) => {
        if (this.refInput.length === 0) return;
        if (!pos || pos === 'min') {
          this.refInput[0].setSelectionRange(start, end);
          this.refInput[0].focus();
        } else if (pos === 'max') {
          this.refInput[1].setSelectionRange(start, end);
          this.refInput[1].focus();
        }
      });
    },

    unmountPicker () {
      if (this.picker) {
        this.picker.$destroy();
        this.picker.$off();
        if (typeof this.unwatchPickerOptions === 'function') {
          this.unwatchPickerOptions();
        }
        this.picker.$el.parentNode.removeChild(this.picker.$el);
      }
    },

    emitChange (val) {
      // determine user real change only
      if (!valueEquals(val, this.valueOnOpen)) {
        this.$emit('change', val);
        this.valueOnOpen = val;
        if (this.validateEvent) {
          this.dispatch('ElFormItem', 'el.form.change', val);
        }
      }
    },

    emitInput (val) {
      const formatted = this.formatToValue(val);
      if (!valueEquals(this.value, formatted)) {
        this.$emit('input', formatted);
      }
    },

    isValidValue (value) {
      if (!this.picker) {
        this.mountPicker();
      }
      if (this.picker.isValidValue) {
        return value && this.picker.isValidValue(value);
      } else {
        return true;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// picker.vue?6fdd9963